import moment from "moment";

export interface payRecords {
  is_Duplicate: number;
  Pay_frequency: { value: string } | null;
  Pay: number | string | null;
  Start_date?: string | null;
  End_date?: string | null;
}

export const VALID_PAY_FREQUENCIES = {
  Monthly: 'Monthly',
  Weekly: 'Weekly',
  Bi_Weekly: 'Bi-Weekly',
  Bi_Monthly: 'Bi-Monthly',
  Quarterly: 'Quarterly',
  Yearly: 'Yearly',
  OneTime: 'One-Time',
  CustomPeriod: 'Custom Period',
} as const;

const payMultiplier: Record<string, number> = {
  [VALID_PAY_FREQUENCIES.Monthly]: 1,
  [VALID_PAY_FREQUENCIES.Weekly]: 52 / 12,
  [VALID_PAY_FREQUENCIES.Bi_Weekly]: 26 / 12,
  [VALID_PAY_FREQUENCIES.Bi_Monthly]: 2,
  [VALID_PAY_FREQUENCIES.Quarterly]: 1 / 3,
  [VALID_PAY_FREQUENCIES.Yearly]: 1 / 12,
  [VALID_PAY_FREQUENCIES.OneTime]: 1 / 12,
};

const legacyFrequencyMap: Record<string, string> = {
  monthly: VALID_PAY_FREQUENCIES.Monthly,
  weekly: VALID_PAY_FREQUENCIES.Weekly,
  biweekly: VALID_PAY_FREQUENCIES.Bi_Weekly,
  semimonthly: VALID_PAY_FREQUENCIES.Bi_Monthly,
  bimonthly: VALID_PAY_FREQUENCIES.Bi_Monthly,
  quarterly: VALID_PAY_FREQUENCIES.Quarterly,
  annual: VALID_PAY_FREQUENCIES.Yearly,
  yearly: VALID_PAY_FREQUENCIES.Yearly,
  onetime: VALID_PAY_FREQUENCIES.OneTime,
  customperiod: VALID_PAY_FREQUENCIES.CustomPeriod,
};

const DATE_FORMATS = ["YYYY-MM-DD", "YYYY-M-D", "MM/DD/YYYY", "M/D/YYYY"];

export const normalizeFrequency = (value?: string | null): string => {
  const normalized = value ? value.trim().toLowerCase().replace(/[\s_-]/g, "") : "onetime";
  return legacyFrequencyMap[normalized] || "";
};

const inclusiveDays = (startDate?: string | null, endDate?: string | null): number | null => {
  const start = moment(startDate, DATE_FORMATS, true);
  const end = moment(endDate, DATE_FORMATS, true);
  if (!start.isValid() || !end.isValid() || start.isAfter(end)) return null;
  return end.diff(start, "days") + 1;
};

export const roundCurrency = (value: number): number => Math.round(value * 100) / 100;

export const isMonthlyPayRecordIncluded = ({ is_Duplicate, Pay_frequency, Start_date, End_date }: payRecords): boolean => {
  if (is_Duplicate === 1) return false;
  if (!Start_date || !End_date) return false;

  const freqValue = normalizeFrequency(Pay_frequency?.value);
  const days = inclusiveDays(Start_date, End_date);
  if (days === null) return false;

  if (freqValue === VALID_PAY_FREQUENCIES.CustomPeriod) {
    return true;
  }

  return !!freqValue && freqValue in payMultiplier;
};

export const countUniqueMonthlyPayRecords = (payRecords: payRecords[] = []): number => {
  return payRecords
    .filter(isMonthlyPayRecordIncluded)
    .filter((record, index, self) =>
      index === self.findIndex((r) =>
        r.Start_date === record.Start_date &&
        r.End_date === record.End_date
      )
    ).length;
};

export const calculateMonthlyPayTotal = (payRecords: payRecords[] = [], round = true): number => {
  const total = payRecords.reduce((total, { is_Duplicate, Pay_frequency, Pay, Start_date, End_date }) => {
    if (is_Duplicate === 1) return total;
    if (!Start_date || !End_date) return total;
    
    const payAmount = Number(Pay) || 0;
    const freqValue = normalizeFrequency(Pay_frequency?.value);
    const days = inclusiveDays(Start_date, End_date);
    if (days === null) return total;

    if (freqValue === VALID_PAY_FREQUENCIES.CustomPeriod) {
      return total + (payAmount / days) * (365 / 12);
    }
    if (!freqValue || !(freqValue in payMultiplier)) return total;
    const multiplier = payMultiplier[freqValue] ?? 1;
    return total + payAmount * multiplier;
  }, 0);

  return round ? roundCurrency(total) : total;
};

export const calculateMonthlyPayFromAmount = (
  payAmount?: number | string | null,
  payFrequency?: string | null
): number => {
  const amount = Number(payAmount) || 0;
  const freqValue = normalizeFrequency(payFrequency);

  if (!freqValue || !(freqValue in payMultiplier)) {
    return 0;
  }

  const multiplier = payMultiplier[freqValue] ?? 1;
  return roundCurrency(amount * multiplier);
};

const CalculateMonthlyPay = (payRecords: payRecords[] = []): number => {
  return calculateMonthlyPayTotal(payRecords, true);
};

export default CalculateMonthlyPay;
